// src/foundations/chart-palette.ts
var chartPalette = [
  "var(--paper-color-chart-1)",
  "var(--paper-color-chart-2)",
  "var(--paper-color-chart-3)",
  "var(--paper-color-chart-4)",
  "var(--paper-color-chart-5)",
  "var(--paper-color-chart-6)",
  "var(--paper-color-chart-7)",
  "var(--paper-color-chart-8)"
];
var chartCategoryStyles = chartPalette.map((color, index) => ({
  color,
  dash: [[], [6, 3], [2, 2], [8, 3, 2, 3]][index % 4],
  pattern: ["solid", "diagonal", "dots", "crosshatch"][index % 4],
  pointStyle: ["circle", "rect", "triangle", "rectRot"][index % 4]
}));
var chartSeries = chartCategoryStyles;

// src/components/actions/Button/Button.tsx
import {
  forwardRef,
  useId
} from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var BUTTON_VARIANTS = [
  "default",
  "outline",
  "ghost",
  "link",
  "destructive"
];
function buttonClassName({
  variant = "default",
  fullWidth = false,
  loading = false,
  className
} = {}) {
  return [
    "paper-button",
    `paper-button--${variant}`,
    fullWidth ? "paper-button--full-width" : "",
    loading ? "paper-button--loading" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
}
var Button = forwardRef(function Button2({
  variant = "default",
  fullWidth = false,
  loading = false,
  loadingLabel = "Working",
  leadingIcon,
  trailingIcon,
  className,
  children,
  disabled,
  type = "button",
  "aria-describedby": describedBy2,
  "aria-label": ariaLabel,
  ...props
}, ref) {
  const generatedId = useId();
  const loadingStatusId = `paper-button-status-${generatedId.replace(/:/gu, "")}`;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ...props,
      ref,
      type,
      disabled: disabled || loading,
      "aria-busy": loading || void 0,
      "aria-label": ariaLabel ?? (loading && typeof children === "string" ? children : void 0),
      "aria-describedby": [describedBy2, loading ? loadingStatusId : void 0].filter(Boolean).join(" ") || void 0,
      className: buttonClassName({ variant, fullWidth, loading, className }),
      children: [
        loading ? /* @__PURE__ */ jsx("span", { className: "paper-button__spinner", "aria-hidden": "true" }) : null,
        !loading && leadingIcon ? /* @__PURE__ */ jsx("span", { className: "paper-button__icon", "aria-hidden": "true", children: leadingIcon }) : null,
        /* @__PURE__ */ jsx("span", { className: "paper-button__label", children }),
        !loading && trailingIcon ? /* @__PURE__ */ jsx("span", { className: "paper-button__icon", "aria-hidden": "true", children: trailingIcon }) : null,
        loading ? /* @__PURE__ */ jsx("span", { className: "paper-visually-hidden", id: loadingStatusId, role: "status", children: loadingLabel }) : null
      ]
    }
  );
});

// src/components/forms/field.tsx
import { createFormHookContexts, useStore } from "@tanstack/react-form";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();
function useField() {
  const field = useFieldContext();
  const form = useFormContext();
  const submitted = useStore(form.store, (state) => state.submissionAttempts > 0);
  const errors = useStore(field.store, (state) => state.meta.errors);
  const showErrors = submitted || Boolean(field.state.meta.errorMap.onSubmit) || field.state.meta.isTouched && Boolean(field.options.validators?.onBlur);
  const first = showErrors ? errors[0] : void 0;
  const error = typeof first === "string" ? first : first?.message;
  return { field, error };
}
function FieldFrame({
  id,
  label,
  hint,
  error,
  required,
  children
}) {
  return /* @__PURE__ */ jsxs2("div", { className: `paper-field${error ? " paper-field--invalid" : ""}`, children: [
    /* @__PURE__ */ jsxs2("label", { className: "paper-field__label", htmlFor: id, id: `${id}-label`, children: [
      label,
      required ? /* @__PURE__ */ jsx2("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
    ] }),
    children,
    hint ? /* @__PURE__ */ jsx2("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
    error ? /* @__PURE__ */ jsx2("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
  ] });
}
function describedBy(id, hint, error, external) {
  return [hint ? `${id}-hint` : void 0, error ? `${id}-error` : void 0, external].filter(Boolean).join(" ") || void 0;
}

// src/components/forms/Input/Input.tsx
import { forwardRef as forwardRef2, useId as useId2 } from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var Input = forwardRef2(function Input2({
  label,
  hint,
  id: providedId,
  required,
  readOnly,
  disabled,
  className,
  ...props
}, forwardedRef) {
  const { field, error } = useField();
  const generatedId = useId2();
  const id = providedId ?? `paper-input-${generatedId.replace(/:/gu, "")}`;
  const hintId = hint ? `${id}-hint` : void 0;
  const errorId = error ? `${id}-error` : void 0;
  const describedBy2 = [hintId, errorId, props["aria-describedby"]].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: [
        "paper-field",
        error ? "paper-field--invalid" : "",
        disabled ? "paper-field--disabled" : "",
        className ?? ""
      ].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsxs3("label", { className: "paper-field__label", htmlFor: id, children: [
          label,
          required ? /* @__PURE__ */ jsx3("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
        ] }),
        /* @__PURE__ */ jsx3(
          "input",
          {
            ...props,
            name: field.name,
            value: typeof field.state.value === "number" && Number.isNaN(field.state.value) ? "" : field.state.value ?? "",
            onChange: (event) => field.handleChange(props.type === "number" ? event.currentTarget.valueAsNumber : event.currentTarget.value),
            onBlur: field.handleBlur,
            id,
            required,
            readOnly,
            disabled,
            "aria-invalid": error ? true : void 0,
            "aria-describedby": describedBy2,
            className: "paper-input",
            ref: forwardedRef
          }
        ),
        hint ? /* @__PURE__ */ jsx3("p", { className: "paper-field__hint", id: hintId, children: hint }) : null,
        error ? /* @__PURE__ */ jsx3("p", { className: "paper-field__error", id: errorId, role: "alert", children: error || "Check this field." }) : null
      ]
    }
  );
});

// src/components/forms/ToggleSelect.tsx
import { useId as useId3 } from "react";

// src/icons/index.ts
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import {
  CheckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon
} from "@heroicons/react/24/solid";
var iconSizeClassNames = {
  compact: "paper-icon-compact",
  default: "paper-icon-default",
  prominent: "paper-icon-prominent",
  emptyState: "paper-icon-empty-state"
};
function iconClassName(size = "default", className) {
  return [iconSizeClassNames[size], className].filter(Boolean).join(" ");
}

// src/components/forms/ToggleSelect.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function ToggleSelect({ label, options, hint, disabled }) {
  const id = `paper-toggle-${useId3().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const selected = field.state.value || [];
  const describedBy2 = [hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs4(
    "fieldset",
    {
      className: `paper-choice-field paper-toggle-select${error ? " paper-field--invalid" : ""}`,
      disabled,
      "aria-describedby": describedBy2,
      "aria-invalid": error ? true : void 0,
      children: [
        /* @__PURE__ */ jsx4("legend", { className: "paper-field__label", children: label }),
        /* @__PURE__ */ jsx4("div", { className: "paper-toggle-select__options", children: options.map((option) => {
          const pressed = selected.includes(option.value);
          return /* @__PURE__ */ jsxs4(
            "button",
            {
              type: "button",
              className: "paper-toggle-select__option",
              disabled: disabled || option.disabled,
              "aria-pressed": pressed,
              "aria-describedby": describedBy2,
              onBlur: field.handleBlur,
              onClick: () => field.handleChange(pressed ? [] : [option.value]),
              children: [
                /* @__PURE__ */ jsxs4("span", { className: "paper-toggle-select__text", children: [
                  /* @__PURE__ */ jsx4("span", { children: option.label }),
                  " ",
                  option.description ? /* @__PURE__ */ jsx4("span", { className: "paper-toggle-select__description", children: option.description }) : null
                ] }),
                /* @__PURE__ */ jsx4(CheckIcon, { className: "paper-icon-compact paper-toggle-select__check", "aria-hidden": "true" })
              ]
            },
            option.value
          );
        }) }),
        hint ? /* @__PURE__ */ jsx4("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
        error ? /* @__PURE__ */ jsx4("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
      ]
    }
  );
}

// src/components/forms/controls.tsx
import {
  useId as useId4
} from "react";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function TextArea({
  label,
  hint,
  required,
  id: providedId,
  ...props
}) {
  const id = providedId ?? `paper-textarea-${useId4().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsx5(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsx5(
    "textarea",
    {
      ...props,
      name: field.name,
      value: field.state.value ?? "",
      onChange: (event) => field.handleChange(event.currentTarget.value),
      onBlur: field.handleBlur,
      id,
      required,
      "aria-invalid": error ? true : void 0,
      "aria-describedby": describedBy(id, hint, error, props["aria-describedby"]),
      className: "paper-input paper-textarea"
    }
  ) });
}
function Select({
  label,
  hint,
  required,
  options,
  groups,
  placeholder,
  id: providedId,
  ...props
}) {
  const id = providedId ?? `paper-select-${useId4().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsx5(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs5("span", { className: "paper-select__control", children: [
    /* @__PURE__ */ jsxs5(
      "select",
      {
        ...props,
        name: field.name,
        value: field.state.value ?? "",
        onChange: (event) => field.handleChange(event.currentTarget.value),
        onBlur: field.handleBlur,
        id,
        required,
        "aria-invalid": error ? true : void 0,
        "aria-describedby": describedBy(id, hint, error, props["aria-describedby"]),
        className: "paper-input paper-select",
        children: [
          placeholder ? /* @__PURE__ */ jsx5("option", { value: "", children: placeholder }) : null,
          groups ? groups.map((group) => /* @__PURE__ */ jsx5("optgroup", { label: group.label, children: group.options.map((option) => /* @__PURE__ */ jsx5("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value)) }, group.label)) : options.map((option) => /* @__PURE__ */ jsx5("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value))
        ]
      }
    ),
    /* @__PURE__ */ jsx5(
      ChevronDownIcon,
      {
        className: iconClassName("compact", "paper-select__icon"),
        "aria-hidden": "true"
      }
    )
  ] }) });
}
function Checkbox({
  label,
  hint,
  required,
  id: providedId,
  ...props
}) {
  const id = providedId ?? `paper-checkbox-${useId4().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const selected = field.state.value;
  const option = typeof props.value === "string" ? props.value : void 0;
  return /* @__PURE__ */ jsxs5("fieldset", { className: `paper-choice-field${error ? " paper-field--invalid" : ""}`, children: [
    /* @__PURE__ */ jsxs5("label", { className: "paper-choice", htmlFor: id, children: [
      /* @__PURE__ */ jsx5(
        "input",
        {
          ...props,
          name: field.name,
          checked: option === void 0 ? selected === true : Array.isArray(selected) && selected.includes(option),
          onChange: (event) => {
            if (option === void 0) {
              field.handleChange(event.currentTarget.checked);
            } else {
              const values = Array.isArray(selected) ? selected : [];
              field.handleChange(event.currentTarget.checked ? [...values, option] : values.filter((value) => value !== option));
            }
          },
          onBlur: field.handleBlur,
          id,
          type: "checkbox",
          required,
          "aria-invalid": error ? true : void 0,
          "aria-describedby": describedBy(id, hint, error, props["aria-describedby"])
        }
      ),
      /* @__PURE__ */ jsx5("span", { children: label })
    ] }),
    hint ? /* @__PURE__ */ jsx5("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
    error ? /* @__PURE__ */ jsx5("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
  ] });
}

// src/components/forms/choice-controls.tsx
import { useId as useId5 } from "react";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var RADIO_SELECT_VARIANTS = ["default", "segmented"];
function RadioSelect({
  label,
  hint,
  required,
  options,
  disabled,
  variant = "default"
}) {
  const id = `paper-radio-${useId5().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const segmented = variant === "segmented";
  return /* @__PURE__ */ jsxs6(
    "fieldset",
    {
      className: `paper-choice-field paper-radio-select paper-radio-select--${variant}${error ? " paper-field--invalid" : ""}`,
      "aria-describedby": describedBy(id, hint, error),
      "aria-invalid": error ? true : void 0,
      disabled,
      children: [
        /* @__PURE__ */ jsxs6("legend", { className: "paper-field__label", children: [
          label,
          required ? /* @__PURE__ */ jsx6("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
        ] }),
        /* @__PURE__ */ jsx6("div", { className: "paper-choice-list", children: options.map((option) => /* @__PURE__ */ jsxs6("label", { className: segmented ? "paper-radio-select__segment" : "paper-choice", children: [
          /* @__PURE__ */ jsx6(
            "input",
            {
              name: field.name,
              checked: field.state.value === option.value,
              onChange: () => field.handleChange(option.value),
              onBlur: field.handleBlur,
              type: "radio",
              value: option.value,
              disabled: option.disabled,
              required,
              "aria-invalid": error ? true : void 0
            }
          ),
          /* @__PURE__ */ jsx6("span", { className: segmented ? "paper-radio-select__label" : void 0, children: option.label }),
          segmented ? /* @__PURE__ */ jsx6(
            CheckIcon,
            {
              className: iconClassName("compact", "paper-radio-select__check"),
              "aria-hidden": "true"
            }
          ) : null
        ] }, option.value)) }),
        hint ? /* @__PURE__ */ jsx6("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
        error ? /* @__PURE__ */ jsx6("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
      ]
    }
  );
}
function RadioGroup({
  label,
  hint,
  required = true,
  options
}) {
  const id = `paper-radio-group-${useId5().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsxs6(
    "fieldset",
    {
      className: `paper-choice-field${error ? " paper-field--invalid" : ""}`,
      "aria-describedby": describedBy(id, hint, error),
      "aria-invalid": error ? true : void 0,
      children: [
        /* @__PURE__ */ jsxs6("legend", { className: "paper-field__label", children: [
          label,
          required ? /* @__PURE__ */ jsx6("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
        ] }),
        /* @__PURE__ */ jsx6("div", { className: "paper-radio-cards", children: options.map((option) => /* @__PURE__ */ jsxs6("label", { className: "paper-radio-card", children: [
          /* @__PURE__ */ jsx6(
            "input",
            {
              name: field.name,
              checked: field.state.value === option.value,
              onChange: () => field.handleChange(option.value),
              onBlur: field.handleBlur,
              type: "radio",
              value: option.value,
              required,
              disabled: option.disabled,
              "aria-invalid": error ? true : void 0
            }
          ),
          /* @__PURE__ */ jsxs6("span", { children: [
            /* @__PURE__ */ jsx6("strong", { children: option.label }),
            /* @__PURE__ */ jsx6("small", { children: option.description })
          ] })
        ] }, option.value)) }),
        hint ? /* @__PURE__ */ jsx6("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
        error ? /* @__PURE__ */ jsx6("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
      ]
    }
  );
}

// src/components/forms/AmountWithUnit.tsx
import { useId as useId6 } from "react";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
function AmountWithUnit({ label, units, unitsLabel, unitField, hint, required, disabled, ...props }) {
  const id = `paper-amount-${useId6().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsx7(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs7("div", { className: "paper-compound-field", role: "group", "aria-labelledby": `${id}-label`, children: [
    /* @__PURE__ */ jsx7(
      "input",
      {
        ...props,
        name: field.name,
        value: typeof field.state.value === "number" && Number.isNaN(field.state.value) ? "" : field.state.value ?? "",
        onChange: (event) => field.handleChange(event.currentTarget.valueAsNumber),
        onBlur: field.handleBlur,
        required,
        disabled,
        id,
        type: "number",
        className: "paper-input",
        "aria-invalid": error ? true : void 0,
        "aria-describedby": describedBy(id, hint, error, props["aria-describedby"])
      }
    ),
    /* @__PURE__ */ jsx7(
      "select",
      {
        name: unitField.name,
        value: unitField.state.value,
        onChange: (event) => unitField.handleChange(event.currentTarget.value),
        onBlur: unitField.handleBlur,
        disabled,
        "aria-label": unitsLabel ?? `Unit for ${label.toLocaleLowerCase()}`,
        className: "paper-input paper-compound-field__unit",
        "aria-describedby": describedBy(id, hint, error, props["aria-describedby"]),
        children: units.map((unit) => /* @__PURE__ */ jsx7("option", { value: unit.value, disabled: unit.disabled, children: unit.label }, unit.value))
      }
    )
  ] }) });
}

// src/components/forms/autocomplete-controls.tsx
import { Combobox } from "@base-ui/react/combobox";
import { useId as useId7, useMemo, useRef, useState } from "react";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
function useFilteredOptions(options, query, format, match, maxResults = 50) {
  return useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return options.filter((option) => !normalized || (match ? match(option, query) : format(option).toLocaleLowerCase().includes(normalized))).slice(0, maxResults);
  }, [format, match, maxResults, options, query]);
}
function ComboboxPopup({
  options,
  getId,
  format,
  portalContainer
}) {
  return /* @__PURE__ */ jsx8(Combobox.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx8(Combobox.Positioner, { className: "paper-combobox__positioner", sideOffset: 4, children: /* @__PURE__ */ jsxs8(Combobox.Popup, { className: "paper-combobox__popup paper-elevation-floating", children: [
    /* @__PURE__ */ jsx8(Combobox.Empty, { className: "paper-combobox__empty", children: "No matching options." }),
    /* @__PURE__ */ jsx8(Combobox.List, { children: options.map((option, index) => /* @__PURE__ */ jsxs8(
      Combobox.Item,
      {
        value: option,
        index,
        className: "paper-combobox__item",
        children: [
          /* @__PURE__ */ jsx8(Combobox.ItemIndicator, { className: "paper-combobox__indicator", "aria-hidden": "true", children: /* @__PURE__ */ jsx8(CheckIcon, { className: iconClassName("compact") }) }),
          /* @__PURE__ */ jsx8("span", { children: format(option) })
        ]
      },
      getId(option)
    )) })
  ] }) }) });
}
function AutocompleteInput({
  label,
  hint,
  required,
  options,
  format,
  getId,
  match,
  maxResults,
  disabled,
  placeholder
}) {
  const id = `paper-autocomplete-${useId7().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const [query, setQuery] = useState("");
  const [portalContainer, setPortalContainer] = useState(null);
  const filtered = useFilteredOptions(options, query, format, match, maxResults);
  return /* @__PURE__ */ jsx8(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs8(
    Combobox.Root,
    {
      items: options,
      filteredItems: filtered,
      value: field.state.value ?? null,
      onValueChange: (value, details) => {
        if (details.reason === "escape-key") details.cancel();
        else field.handleChange(value);
      },
      onInputValueChange: (value, details) => {
        if (details.reason === "escape-key") details.cancel();
        else setQuery(value);
      },
      itemToStringLabel: format,
      isItemEqualToValue: (left, right) => getId(left) === getId(right),
      disabled,
      children: [
        /* @__PURE__ */ jsxs8("div", { className: "paper-combobox__input-group", children: [
          /* @__PURE__ */ jsx8(
            Combobox.Input,
            {
              id,
              name: field.name,
              onBlur: field.handleBlur,
              "aria-required": required || void 0,
              placeholder,
              className: "paper-input",
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy(id, hint, error),
              ref: (node) => {
                if (node && node.ownerDocument.body !== portalContainer) setPortalContainer(node.ownerDocument.body);
              }
            }
          ),
          /* @__PURE__ */ jsx8(Combobox.Trigger, { className: "paper-combobox__trigger", "aria-label": `Show ${label.toLocaleLowerCase()} options`, children: /* @__PURE__ */ jsx8(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
        ] }),
        /* @__PURE__ */ jsx8(ComboboxPopup, { options: filtered, getId, format, portalContainer })
      ]
    }
  ) });
}
function AutocompleteMultiInput({
  label,
  hint,
  required,
  options,
  format,
  getId,
  match,
  maxResults,
  maxSelections,
  disabled,
  placeholder
}) {
  const id = `paper-multiautocomplete-${useId7().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const values = field.state.value ?? [];
  const [query, setQuery] = useState("");
  const [portalContainer, setPortalContainer] = useState(null);
  const filtered = useFilteredOptions(options, query, format, match, maxResults);
  const atLimit = maxSelections !== void 0 && values.length >= maxSelections;
  return /* @__PURE__ */ jsx8(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs8(
    Combobox.Root,
    {
      multiple: true,
      items: options,
      filteredItems: filtered,
      value: values,
      onValueChange: (value, details) => {
        if (details.reason === "escape-key") details.cancel();
        else if (maxSelections === void 0 || value.length <= maxSelections || value.length < values.length) field.handleChange(value);
      },
      onInputValueChange: (value, details) => {
        if (details.reason === "escape-key") details.cancel();
        else setQuery(value);
      },
      itemToStringLabel: format,
      isItemEqualToValue: (left, right) => getId(left) === getId(right),
      disabled,
      children: [
        /* @__PURE__ */ jsxs8(Combobox.Chips, { className: "paper-combobox__chips", children: [
          values.map((value) => /* @__PURE__ */ jsxs8(Combobox.Chip, { className: "paper-combobox__chip", children: [
            format(value),
            /* @__PURE__ */ jsx8(Combobox.ChipRemove, { "aria-label": `Remove ${format(value)}`, children: /* @__PURE__ */ jsx8(XMarkIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
          ] }, getId(value))),
          /* @__PURE__ */ jsx8(
            Combobox.Input,
            {
              id,
              name: field.name,
              onBlur: field.handleBlur,
              "aria-required": required || void 0,
              placeholder: atLimit ? "Maximum selections reached" : placeholder,
              className: "paper-combobox__chip-input",
              disabled: disabled || atLimit,
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy(id, hint, error),
              ref: (node) => {
                if (node && node.ownerDocument.body !== portalContainer) setPortalContainer(node.ownerDocument.body);
              }
            }
          ),
          /* @__PURE__ */ jsx8(Combobox.Trigger, { disabled: disabled || atLimit, className: "paper-combobox__trigger", "aria-label": `Show ${label.toLocaleLowerCase()} options`, children: /* @__PURE__ */ jsx8(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
        ] }),
        /* @__PURE__ */ jsx8(ComboboxPopup, { options: filtered, getId, format, portalContainer })
      ]
    }
  ) });
}
function TagsInput({
  label,
  hint,
  required,
  options,
  match,
  maxResults = 50,
  maxSelections,
  disabled,
  placeholder = "Type to add a tag"
}) {
  const id = `paper-tags-${useId7().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const values = field.state.value ?? [];
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);
  const chipsRef = useRef(null);
  const composing = useRef(false);
  const atLimit = maxSelections !== void 0 && values.length >= maxSelections;
  const tagKey = (value) => value.trim().toLocaleLowerCase();
  const candidate = query.trim();
  const knownTags = [...new Map(options.filter((value) => value.trim()).map((value) => [tagKey(value), value.trim()])).values()];
  const alreadySelected = values.some((value) => tagKey(value) === tagKey(candidate));
  const existingTag = knownTags.find((value) => tagKey(value) === tagKey(candidate));
  const canCreate = Boolean(candidate) && !alreadySelected && !existingTag;
  const suggestions = knownTags.filter(
    (value) => !values.some((selected) => tagKey(selected) === tagKey(value)) && (!candidate || (match ? match(value, query) : tagKey(value).includes(tagKey(candidate))))
  ).slice(0, maxResults);
  const items = canCreate ? [...suggestions, candidate] : suggestions;
  return /* @__PURE__ */ jsx8(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs8(
    Combobox.Root,
    {
      multiple: true,
      items,
      filteredItems: items,
      value: values,
      inputValue: query,
      open: open && !atLimit,
      onOpenChange: setOpen,
      onValueChange: (value, details) => {
        if (details.reason === "escape-key") details.cancel();
        else if (maxSelections === void 0 || value.length <= maxSelections || value.length < values.length) {
          field.handleChange(value);
          if (value.length > values.length) {
            setQuery("");
            setOpen(false);
          }
        }
      },
      onInputValueChange: (value, details) => {
        if (details.reason === "escape-key") details.cancel();
        else setQuery(value);
      },
      disabled,
      children: [
        /* @__PURE__ */ jsxs8(Combobox.Chips, { ref: chipsRef, className: "paper-combobox__chips paper-tags-input", children: [
          values.map((value) => /* @__PURE__ */ jsxs8(Combobox.Chip, { className: "paper-combobox__chip", children: [
            value,
            /* @__PURE__ */ jsx8(Combobox.ChipRemove, { "aria-label": `Remove ${value}`, children: /* @__PURE__ */ jsx8(XMarkIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
          ] }, value)),
          /* @__PURE__ */ jsx8(
            Combobox.Input,
            {
              id,
              name: field.name,
              onBlur: field.handleBlur,
              onCompositionStart: () => {
                composing.current = true;
              },
              onCompositionEnd: () => {
                composing.current = false;
              },
              onKeyDown: (event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                if (composing.current || event.nativeEvent.isComposing || event.keyCode === 229 || atLimit) {
                  event.preventBaseUIHandler();
                  return;
                }
                if (event.currentTarget.getAttribute("aria-activedescendant")) return;
                event.preventBaseUIHandler();
                if (candidate && !alreadySelected) {
                  field.handleChange([...values, existingTag ?? candidate]);
                  setQuery("");
                  setOpen(false);
                }
              },
              "aria-required": required || void 0,
              placeholder: atLimit ? "Maximum tags reached" : placeholder,
              className: "paper-combobox__chip-input",
              disabled,
              readOnly: atLimit,
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy(id, hint, error),
              ref: (node) => {
                if (node && node.ownerDocument.body !== portalContainer) setPortalContainer(node.ownerDocument.body);
              }
            }
          ),
          /* @__PURE__ */ jsx8(Combobox.Trigger, { disabled: disabled || atLimit, className: "paper-combobox__trigger", "aria-label": `Show ${label.toLocaleLowerCase()} options`, children: /* @__PURE__ */ jsx8(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
        ] }),
        /* @__PURE__ */ jsx8(Combobox.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx8(Combobox.Positioner, { anchor: chipsRef, align: "start", className: "paper-combobox__positioner", sideOffset: 4, children: /* @__PURE__ */ jsxs8(Combobox.Popup, { className: "paper-combobox__popup paper-elevation-floating", children: [
          /* @__PURE__ */ jsx8(Combobox.Empty, { className: "paper-combobox__empty", children: alreadySelected ? "This tag is already added." : "Type to add a tag." }),
          /* @__PURE__ */ jsx8(Combobox.List, { children: items.map((value, index) => /* @__PURE__ */ jsx8(Combobox.Item, { value, index, className: "paper-combobox__item paper-tags-input__option", children: canCreate && value === candidate ? `Add \u201C${value}\u201D` : value }, value)) })
        ] }) }) })
      ]
    }
  ) });
}

// src/components/forms/form.tsx
import { createFormHook } from "@tanstack/react-form";
import { jsx as jsx9 } from "react/jsx-runtime";
function Form({ children, ...props }) {
  const form = useFormContext();
  return /* @__PURE__ */ jsx9("form", { ...props, noValidate: true, onSubmit: async (event) => {
    event.preventDefault();
    if (form.state.isSubmitting) return;
    const element = event.currentTarget;
    await form.handleSubmit();
    if (!form.state.isValid) {
      element.querySelector('input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"], [aria-invalid="true"] button:not(:disabled)')?.focus();
    }
  }, children });
}
var { useAppForm: usePaperForm, withForm: withPaperForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: { Input, Checkbox, Select, TextArea, RadioGroup, RadioSelect, ToggleSelect, AutocompleteInput, AutocompleteMultiInput, TagsInput, AmountWithUnit },
  formComponents: { Form }
});

// src/components/feedback/feedback.tsx
import { Toast } from "@base-ui/react/toast";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var FLASH_VARIANTS = ["information", "success", "warning", "danger"];
function Flash({
  variant = "information",
  title,
  icon,
  action,
  visible = true,
  children,
  className,
  ...props
}) {
  if (!visible) return null;
  return /* @__PURE__ */ jsxs9(
    "div",
    {
      ...props,
      className: `paper-flash paper-flash--${variant}${className ? ` ${className}` : ""}`,
      role: variant === "danger" ? "alert" : "status",
      children: [
        icon ? /* @__PURE__ */ jsx10("span", { className: "paper-flash__icon", "aria-hidden": "true", children: icon }) : null,
        /* @__PURE__ */ jsxs9("div", { className: "paper-flash__content", children: [
          title ? /* @__PURE__ */ jsx10("strong", { children: title }) : null,
          /* @__PURE__ */ jsx10("div", { children })
        ] }),
        action ? /* @__PURE__ */ jsx10("div", { className: "paper-flash__action", children: action }) : null
      ]
    }
  );
}
function Loading({
  label = "Loading",
  size = "default",
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs9("div", { ...props, className: `paper-loading paper-loading--${size}${className ? ` ${className}` : ""}`, role: "status", children: [
    /* @__PURE__ */ jsx10("span", { className: "paper-loading__spinner", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx10("span", { className: "paper-visually-hidden", children: label })
  ] });
}
function surfaceClassName({
  elevation = "flat",
  accent = false,
  className
} = {}) {
  return [
    "paper-surface-card",
    `paper-elevation-${elevation}`,
    accent ? "paper-accent-rail" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
}
function Surface({
  as: Element = "div",
  elevation = "flat",
  accent = false,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(Element, { ...props, className: surfaceClassName({ elevation, accent, className }) });
}
function ButtonGroup({ actions, label = "Actions", align = "start" }) {
  return /* @__PURE__ */ jsx10("div", { className: `paper-button-group paper-button-group--${align}`, role: "group", "aria-label": label, children: actions.filter((action) => action.visible !== false).map((action) => {
    const className = buttonClassName({ variant: action.variant });
    return action.href ? /* @__PURE__ */ jsx10(
      "a",
      {
        href: action.href,
        className,
        "aria-disabled": action.disabled || void 0,
        onClick: (event) => {
          if (action.disabled) event.preventDefault();
          else action.onSelect?.();
        },
        children: action.label
      },
      action.id
    ) : /* @__PURE__ */ jsx10(
      "button",
      {
        type: "button",
        className,
        disabled: action.disabled,
        onClick: action.onSelect,
        children: action.label
      },
      action.id
    );
  }) });
}
function PaperToastViewport() {
  const manager = Toast.useToastManager();
  return /* @__PURE__ */ jsx10(Toast.Viewport, { className: "paper-toast-viewport", children: manager.toasts.map((toast) => /* @__PURE__ */ jsxs9(Toast.Root, { toast, className: "paper-toast paper-elevation-floating", children: [
    /* @__PURE__ */ jsxs9(Toast.Content, { className: "paper-toast__content", children: [
      toast.title ? /* @__PURE__ */ jsx10(Toast.Title, { className: "paper-toast__title", children: toast.title }) : null,
      toast.description ? /* @__PURE__ */ jsx10(Toast.Description, { children: toast.description }) : null
    ] }),
    toast.actionProps ? /* @__PURE__ */ jsx10(Toast.Action, { className: buttonClassName({ variant: "outline" }), ...toast.actionProps }) : null,
    /* @__PURE__ */ jsx10(
      "button",
      {
        type: "button",
        className: "paper-toast__close",
        "aria-label": "Dismiss notification",
        onClick: () => manager.close(toast.id),
        children: /* @__PURE__ */ jsx10(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" })
      }
    )
  ] }, toast.id)) });
}
function ToastProvider({ children, timeout, limit }) {
  return /* @__PURE__ */ jsxs9(Toast.Provider, { timeout, limit, children: [
    children,
    /* @__PURE__ */ jsx10(PaperToastViewport, {})
  ] });
}
var ToastContainer = ToastProvider;
function useToast() {
  return Toast.useToastManager();
}

// src/components/forms/AppliedFilters/AppliedFilters.tsx
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
function AppliedFilters({
  filters,
  onClear,
  label = "Applied filters",
  clearLabel = "Clear all",
  className
}) {
  if (filters.length === 0) return null;
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: className ? `paper-applied-filters ${className}` : "paper-applied-filters",
      role: "group",
      "aria-label": label,
      children: [
        /* @__PURE__ */ jsx11("p", { className: "paper-applied-filters__list", children: filters.join(" \xB7 ") }),
        /* @__PURE__ */ jsx11(Button, { variant: "link", onClick: onClear, children: clearLabel })
      ]
    }
  );
}

// src/components/feedback/EmptyState/EmptyState.tsx
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
function EmptyState({
  title,
  headingLevel,
  description,
  actions,
  variant = "surface",
  headingRef,
  className,
  ...props
}) {
  const Heading = `h${headingLevel}`;
  const typeRole = headingLevel === 1 ? "paper-type-page" : headingLevel === 2 ? "paper-type-section" : "paper-type-component";
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      ...props,
      className: `paper-empty-state paper-empty-state--${variant}${className ? ` ${className}` : ""}`,
      children: [
        /* @__PURE__ */ jsx12(
          Heading,
          {
            ref: headingRef,
            tabIndex: headingRef ? -1 : void 0,
            className: `paper-empty-state__title ${typeRole}`,
            children: title
          }
        ),
        description ? /* @__PURE__ */ jsx12("p", { className: "paper-empty-state__description", children: description }) : null,
        actions ? /* @__PURE__ */ jsx12("div", { className: "paper-empty-state__actions", children: actions }) : null
      ]
    }
  );
}

// src/components/feedback/StatusTag/StatusTag.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
function StatusTag({ tone = "neutral", children, className }) {
  return /* @__PURE__ */ jsx13("span", { className: `paper-status-tag paper-status-tag--${tone}${className ? ` ${className}` : ""}`, children });
}

// src/components/page-structure/PageHeader/PageHeader.tsx
import { Fragment, jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
function nativeLink(props) {
  return /* @__PURE__ */ jsx14("a", { ...props });
}
function PageHeader({
  title,
  lead,
  status,
  actions,
  backLink,
  renderLink = nativeLink,
  children,
  className
}) {
  return /* @__PURE__ */ jsxs12("header", { className: className ? `paper-page-header ${className}` : "paper-page-header", children: [
    backLink ? renderLink({
      href: backLink.href,
      className: "paper-page-header__back",
      children: /* @__PURE__ */ jsxs12(Fragment, { children: [
        /* @__PURE__ */ jsx14(ChevronLeftIcon, { className: iconClassName("compact"), "aria-hidden": "true" }),
        /* @__PURE__ */ jsx14("span", { children: backLink.label })
      ] })
    }) : null,
    /* @__PURE__ */ jsxs12("div", { className: "paper-page-header__main", children: [
      /* @__PURE__ */ jsxs12("div", { className: "paper-page-header__text", children: [
        /* @__PURE__ */ jsxs12("div", { className: "paper-page-header__heading", children: [
          /* @__PURE__ */ jsx14("h1", { className: "paper-page-header__title paper-type-page", children: title }),
          status ? /* @__PURE__ */ jsx14("div", { className: "paper-page-header__status", children: status }) : null
        ] }),
        lead ? /* @__PURE__ */ jsx14("p", { className: "paper-page-header__lead", children: lead }) : null,
        children
      ] }),
      actions ? /* @__PURE__ */ jsx14("div", { className: "paper-page-header__actions", children: actions }) : null
    ] })
  ] });
}

// src/components/page-structure/SectionHeading/SectionHeading.tsx
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
function SectionHeading({
  level,
  title,
  id,
  description,
  status,
  actions,
  className
}) {
  const Heading = `h${level}`;
  const typeRole = level === 2 ? "paper-type-section" : "paper-type-component";
  return /* @__PURE__ */ jsxs13("div", { className: className ? `paper-section-heading ${className}` : "paper-section-heading", children: [
    /* @__PURE__ */ jsxs13("div", { className: "paper-section-heading__text", children: [
      /* @__PURE__ */ jsxs13("div", { className: "paper-section-heading__heading", children: [
        /* @__PURE__ */ jsx15(Heading, { id, className: `paper-section-heading__title ${typeRole}`, children: title }),
        status ? /* @__PURE__ */ jsx15("div", { className: "paper-section-heading__status", children: status }) : null
      ] }),
      description ? /* @__PURE__ */ jsx15("p", { className: "paper-section-heading__description", children: description }) : null
    ] }),
    actions ? /* @__PURE__ */ jsx15("div", { className: "paper-section-heading__actions", children: actions }) : null
  ] });
}

// src/components/data-display/DescriptionList/DescriptionList.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
function DescriptionList({ items, layout = "stacked", className }) {
  return /* @__PURE__ */ jsx16("dl", { className: `paper-description-list paper-description-list--${layout}${className ? ` ${className}` : ""}`, children: items.map((item, index) => /* @__PURE__ */ jsxs14("div", { className: "paper-description-list__item", children: [
    /* @__PURE__ */ jsx16("dt", { className: "paper-description-list__label", children: item.label }),
    /* @__PURE__ */ jsx16("dd", { className: "paper-description-list__value", children: item.value })
  ] }, index)) });
}

// src/components/data-display/StatGrid/StatGrid.tsx
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
function StatGrid({ stats, className }) {
  return /* @__PURE__ */ jsx17("dl", { className: className ? `paper-stat-grid ${className}` : "paper-stat-grid", children: stats.map((stat, index) => /* @__PURE__ */ jsxs15("div", { className: "paper-stat-grid__item", children: [
    /* @__PURE__ */ jsx17("dt", { className: "paper-stat-grid__label", children: stat.label }),
    /* @__PURE__ */ jsxs15("dd", { className: "paper-stat-grid__value", children: [
      stat.value,
      stat.detail ? /* @__PURE__ */ jsx17("span", { className: "paper-stat-grid__detail", children: stat.detail }) : null
    ] })
  ] }, index)) });
}

// src/components/overlays/Modal/Modal.tsx
import { Dialog } from "@base-ui/react/dialog";
import {
  useState as useState2
} from "react";
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
function Modal({
  triggerLabel,
  trigger,
  triggerVariant = "default",
  title,
  description,
  children,
  closeLabel = "Close",
  action,
  footer,
  defaultOpen,
  open,
  onOpenChange,
  initialFocus
}) {
  const [portalContainer, setPortalContainer] = useState2(null);
  const captureTrigger = (node) => {
    if (node && node.ownerDocument.body !== portalContainer) {
      setPortalContainer(node.ownerDocument.body);
    }
  };
  return /* @__PURE__ */ jsxs16(
    Dialog.Root,
    {
      defaultOpen,
      open,
      onOpenChange: (nextOpen) => onOpenChange?.(nextOpen),
      children: [
        trigger ? /* @__PURE__ */ jsx18(Dialog.Trigger, { ref: captureTrigger, render: trigger }) : /* @__PURE__ */ jsx18(
          Dialog.Trigger,
          {
            ref: captureTrigger,
            className: buttonClassName({ variant: triggerVariant }),
            children: triggerLabel
          }
        ),
        /* @__PURE__ */ jsxs16(Dialog.Portal, { container: portalContainer, children: [
          /* @__PURE__ */ jsx18(Dialog.Backdrop, { className: "paper-modal__backdrop" }),
          /* @__PURE__ */ jsx18(Dialog.Viewport, { className: "paper-modal__viewport", children: /* @__PURE__ */ jsxs16(
            Dialog.Popup,
            {
              className: "paper-modal paper-surface-raised paper-elevation-showcase",
              initialFocus,
              children: [
                /* @__PURE__ */ jsxs16("header", { className: "paper-modal__header", children: [
                  /* @__PURE__ */ jsxs16("div", { children: [
                    /* @__PURE__ */ jsx18(Dialog.Title, { className: "paper-modal__title", children: title }),
                    description ? /* @__PURE__ */ jsx18(Dialog.Description, { className: "paper-modal__description", children: description }) : null
                  ] }),
                  /* @__PURE__ */ jsx18(
                    Dialog.Close,
                    {
                      className: "paper-modal__icon-close",
                      "aria-label": closeLabel,
                      children: /* @__PURE__ */ jsx18(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx18("div", { className: "paper-modal__content", children }),
                footer === void 0 ? /* @__PURE__ */ jsxs16("footer", { className: "paper-modal__footer", children: [
                  action ? /* @__PURE__ */ jsx18(
                    Dialog.Close,
                    {
                      className: buttonClassName({ variant: action.variant }),
                      disabled: action.disabled,
                      onClick: action.onAction,
                      children: action.label
                    }
                  ) : null,
                  /* @__PURE__ */ jsx18(Dialog.Close, { className: buttonClassName({ variant: "outline" }), children: closeLabel })
                ] }) : footer === null ? null : /* @__PURE__ */ jsx18("footer", { className: "paper-modal__footer", children: footer })
              ]
            }
          ) })
        ] })
      ]
    }
  );
}

// src/components/overlays/Drawer/Drawer.tsx
import { Dialog as Dialog2 } from "@base-ui/react/dialog";
import {
  useState as useState3
} from "react";
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var DRAWER_PLACEMENTS = ["start", "end"];
function Drawer({
  trigger,
  title,
  description,
  children,
  footer,
  placement = "end",
  closeLabel = "Close",
  defaultOpen,
  open,
  onOpenChange
}) {
  const [portalContainer, setPortalContainer] = useState3(null);
  return /* @__PURE__ */ jsxs17(
    Dialog2.Root,
    {
      defaultOpen,
      modal: true,
      open,
      onOpenChange: (nextOpen) => onOpenChange?.(nextOpen),
      children: [
        /* @__PURE__ */ jsx19(
          Dialog2.Trigger,
          {
            render: trigger,
            ref: (node) => {
              if (node && node.ownerDocument.body !== portalContainer) {
                setPortalContainer(node.ownerDocument.body);
              }
            }
          }
        ),
        /* @__PURE__ */ jsxs17(Dialog2.Portal, { container: portalContainer, children: [
          /* @__PURE__ */ jsx19(Dialog2.Backdrop, { className: "paper-drawer__backdrop" }),
          /* @__PURE__ */ jsx19(
            Dialog2.Viewport,
            {
              className: "paper-drawer__viewport",
              "data-placement": placement,
              children: /* @__PURE__ */ jsxs17(
                Dialog2.Popup,
                {
                  className: "paper-drawer paper-surface-raised paper-elevation-showcase",
                  "data-placement": placement,
                  children: [
                    /* @__PURE__ */ jsxs17("header", { className: "paper-drawer__header", children: [
                      /* @__PURE__ */ jsxs17("div", { className: "paper-drawer__heading", children: [
                        /* @__PURE__ */ jsx19(Dialog2.Title, { className: "paper-drawer__title", children: title }),
                        description ? /* @__PURE__ */ jsx19(Dialog2.Description, { className: "paper-drawer__description", children: description }) : null
                      ] }),
                      /* @__PURE__ */ jsx19(
                        Dialog2.Close,
                        {
                          className: buttonClassName({
                            variant: "ghost",
                            className: "paper-drawer__close"
                          }),
                          "aria-label": closeLabel,
                          children: /* @__PURE__ */ jsx19(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" })
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx19("div", { className: "paper-drawer__body", children }),
                    footer ? /* @__PURE__ */ jsx19("footer", { className: "paper-drawer__footer", children: footer }) : null
                  ]
                }
              )
            }
          )
        ] })
      ]
    }
  );
}

// src/components/overlays/ActionMenu/ActionMenu.tsx
import { Menu } from "@base-ui/react/menu";
import { useState as useState4 } from "react";
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
function ActionMenu({
  label,
  items,
  triggerVariant = "outline",
  defaultOpen,
  iconOnly = false,
  triggerIcon
}) {
  const [portalContainer, setPortalContainer] = useState4(null);
  return /* @__PURE__ */ jsxs18(Menu.Root, { defaultOpen, children: [
    /* @__PURE__ */ jsxs18(
      Menu.Trigger,
      {
        ref: (node) => {
          if (node && node.ownerDocument.body !== portalContainer) {
            setPortalContainer(node.ownerDocument.body);
          }
        },
        className: buttonClassName({ variant: triggerVariant, className: iconOnly ? "paper-action-menu__trigger--icon-only" : void 0 }),
        "aria-label": iconOnly ? label : void 0,
        title: iconOnly ? label : void 0,
        children: [
          !iconOnly && /* @__PURE__ */ jsx20("span", { children: label }),
          triggerIcon ? /* @__PURE__ */ jsx20("span", { className: "paper-action-menu__icon", "aria-hidden": "true", children: triggerIcon }) : iconOnly ? /* @__PURE__ */ jsx20(EllipsisHorizontalIcon, { className: iconClassName(), "aria-hidden": "true" }) : /* @__PURE__ */ jsx20(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" })
        ]
      }
    ),
    /* @__PURE__ */ jsx20(Menu.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx20(
      Menu.Positioner,
      {
        className: "paper-action-menu__positioner",
        sideOffset: 6,
        align: "end",
        children: /* @__PURE__ */ jsx20(Menu.Popup, { className: "paper-action-menu paper-elevation-floating", children: items.map((item) => /* @__PURE__ */ jsxs18(
          Menu.Item,
          {
            className: `paper-action-menu__item${item.destructive ? " paper-action-menu__item--destructive" : ""}`,
            disabled: item.disabled,
            label: item.label,
            onClick: item.onSelect,
            children: [
              item.icon ? /* @__PURE__ */ jsx20("span", { className: "paper-action-menu__icon", "aria-hidden": "true", children: item.icon }) : null,
              /* @__PURE__ */ jsx20("span", { children: item.label })
            ]
          },
          item.id
        )) })
      }
    ) })
  ] });
}

// src/components/data-display/Table.tsx
import {
  useId as useId8
} from "react";
import { jsx as jsx21, jsxs as jsxs19 } from "react/jsx-runtime";
import { createElement } from "react";
function classNames(...values) {
  return values.filter(Boolean).join(" ");
}
function Table({
  caption,
  captionVisibility = "visible",
  columns,
  rows,
  getRowKey,
  getRowProps,
  emptyMessage = "No data to display.",
  minWidth = `${Math.max(30, columns.length * 9)}rem`,
  tableClassName,
  className,
  style,
  ...regionProps
}) {
  if (columns.length === 0) {
    throw new Error("Table requires at least one column.");
  }
  const reactId = useId8();
  const captionId = `paper-table-caption-${reactId.replace(/:/gu, "")}`;
  const regionStyle = {
    ...style,
    "--paper-table-min-inline-size": minWidth
  };
  return /* @__PURE__ */ jsx21(
    "div",
    {
      ...regionProps,
      className: classNames("paper-table-region", "paper-focus-ring", className),
      style: regionStyle,
      role: "region",
      "aria-labelledby": captionId,
      tabIndex: 0,
      children: /* @__PURE__ */ jsxs19("table", { className: classNames("paper-table", tableClassName), children: [
        /* @__PURE__ */ jsx21(
          "caption",
          {
            id: captionId,
            className: classNames(
              "paper-table__caption",
              captionVisibility === "screen-reader" && "paper-table__caption--screen-reader"
            ),
            children: caption
          }
        ),
        /* @__PURE__ */ jsx21("thead", { children: /* @__PURE__ */ jsx21("tr", { children: columns.map((column) => /* @__PURE__ */ jsx21(
          "th",
          {
            scope: "col",
            className: classNames(
              "paper-table__header",
              `paper-table__cell--${column.align ?? "start"}`
            ),
            style: column.width ? { width: column.width } : void 0,
            children: column.header
          },
          column.id
        )) }) }),
        /* @__PURE__ */ jsx21("tbody", { children: rows.length === 0 ? /* @__PURE__ */ jsx21("tr", { children: /* @__PURE__ */ jsx21("td", { className: "paper-table__empty", colSpan: columns.length, children: emptyMessage }) }) : rows.map((row, rowIndex) => /* @__PURE__ */ createElement("tr", { ...getRowProps?.(row, rowIndex), key: getRowKey?.(row, rowIndex) ?? rowIndex }, columns.map((column) => {
          const Cell2 = column.rowHeader ? "th" : "td";
          return /* @__PURE__ */ jsx21(
            Cell2,
            {
              ...column.rowHeader ? { scope: "row" } : {},
              className: classNames(
                "paper-table__cell",
                column.rowHeader && "paper-table__row-header",
                `paper-table__cell--${column.align ?? "start"}`
              ),
              children: column.cell(row, rowIndex)
            },
            column.id
          );
        }))) })
      ] })
    }
  );
}

// src/components/data-display/HeatmapChart.tsx
import { DateTime, Interval } from "luxon";
import {
  useEffect,
  useRef as useRef2,
  useState as useState5
} from "react";
import { createPortal } from "react-dom";
import { Fragment as Fragment2, jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var CELL_WIDTH = 10;
var CELL_HEIGHT = 10;
var CELL_GAP = 3;
var OFFSET = { x: 30, y: 15 };
var WEEKDAYS = ["Mon", void 0, "Wed", void 0, "Fri", void 0, "Sun"];
function HeatmapChart({ id, data, year }) {
  const start = DateTime.fromObject({ year, month: 1, day: 1 });
  const end = DateTime.fromObject({ year, month: 12, day: 31 });
  const values = /* @__PURE__ */ new Map();
  const tooltips = /* @__PURE__ */ new Map();
  for (const datum of data) {
    values.set(datum.date, datum.value);
    if (datum.tooltip) tooltips.set(datum.date, datum.tooltip);
  }
  const dates = Interval.fromDateTimes(start, end.endOf("day")).splitBy({ day: 1 }).map((interval) => interval.start);
  const weekCount = start.weeksInWeekYear + 1;
  const columns = Array.from(
    { length: weekCount },
    () => Array.from({ length: 7 })
  );
  let columnIndex = 0;
  for (const date of dates) {
    if (date === null) continue;
    const isoDate = date.toISODate();
    if (isoDate === null) continue;
    columns[columnIndex][date.weekday - 1] = {
      date,
      value: values.get(isoDate) ?? 0,
      tooltip: tooltips.get(isoDate)
    };
    if (date.weekday === 7) columnIndex += 1;
  }
  const maxValue = Math.max(
    ...columns.flatMap((column) => column.map((cell) => cell?.value ?? 0))
  );
  let lastMonth = 0;
  const months = columns.map((column) => {
    const shouldRender = column.filter((cell) => cell !== void 0).some((cell) => cell.date.month > lastMonth);
    if (!shouldRender) return void 0;
    lastMonth += 1;
    return DateTime.fromObject({ month: lastMonth }).toFormat("LLL");
  });
  const tooltipLayerId = `paper-heatmap-tooltip-${id}`;
  const titleId = `paper-heatmap-title-${id}`;
  const width = weekCount * CELL_WIDTH + (weekCount - 1) * CELL_GAP + OFFSET.x + 10;
  const height = 7 * CELL_HEIGHT + 6 * CELL_GAP + OFFSET.y;
  return /* @__PURE__ */ jsxs20(
    "svg",
    {
      width,
      height,
      viewBox: `0 0 ${width} ${height}`,
      className: "paper-heatmap",
      role: "img",
      "aria-labelledby": titleId,
      "data-year": year,
      children: [
        /* @__PURE__ */ jsxs20("title", { id: titleId, children: [
          "Daily activity for ",
          year
        ] }),
        WEEKDAYS.map((label, row) => label ? /* @__PURE__ */ jsx22(
          "text",
          {
            className: "paper-heatmap__axis-label",
            textAnchor: "end",
            x: OFFSET.x - CELL_GAP * 2,
            y: OFFSET.y + CELL_HEIGHT * row + CELL_GAP * row,
            dominantBaseline: "hanging",
            children: label
          },
          label
        ) : null),
        months.map((label, column) => label ? /* @__PURE__ */ jsx22(
          "text",
          {
            className: "paper-heatmap__axis-label",
            textAnchor: "start",
            x: OFFSET.x + CELL_WIDTH * column + CELL_GAP * column,
            y: 0,
            dominantBaseline: "hanging",
            children: label
          },
          label
        ) : null),
        columns.map(
          (column, columnPosition) => column.map((cell, rowPosition) => /* @__PURE__ */ jsx22(
            Cell,
            {
              cell,
              tooltipLayerId,
              maxValue,
              column: columnPosition,
              row: rowPosition,
              parentWidth: width
            },
            `${columnPosition}-${rowPosition}`
          ))
        ),
        /* @__PURE__ */ jsx22("g", { id: tooltipLayerId, className: "paper-heatmap__tooltip-layer" })
      ]
    }
  );
}
function Cell({
  cell,
  tooltipLayerId,
  maxValue,
  row,
  column,
  parentWidth
}) {
  const [mounted, setMounted] = useState5(false);
  const [tooltipVisible, setTooltipVisible] = useState5(false);
  const x = OFFSET.x + CELL_WIDTH * column + CELL_GAP * column;
  const y = OFFSET.y + CELL_HEIGHT * row + CELL_GAP * row;
  const isoDate = cell?.date.toISODate() ?? void 0;
  const tooltip = cell && cell.value !== 0 ? cell.tooltip : void 0;
  const tooltipId = `${tooltipLayerId}-${column}-${row}`;
  const level = getCellDepth(maxValue, cell?.value);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  const rect = /* @__PURE__ */ jsx22(
    "rect",
    {
      width: CELL_WIDTH,
      height: CELL_HEIGHT,
      x,
      y,
      className: "paper-heatmap__cell",
      "data-level": level,
      "data-date": isoDate,
      "data-value": cell?.value,
      "aria-hidden": cell === void 0 ? true : void 0,
      "aria-label": cell ? cell.tooltip ?? `${isoDate}: ${cell.value}` : void 0,
      "aria-describedby": tooltip && tooltipVisible ? tooltipId : void 0,
      role: cell ? "graphics-symbol" : void 0,
      tabIndex: tooltip ? 0 : void 0,
      onMouseOver: tooltip ? () => setTooltipVisible(true) : void 0,
      onMouseOut: tooltip ? () => setTooltipVisible(false) : void 0,
      onFocus: tooltip ? () => setTooltipVisible(true) : void 0,
      onBlur: tooltip ? () => setTooltipVisible(false) : void 0,
      onTouchStart: tooltip ? () => setTooltipVisible(true) : void 0,
      onTouchMove: tooltip ? () => setTooltipVisible(true) : void 0,
      onTouchEnd: tooltip ? () => setTooltipVisible(false) : void 0,
      onTouchCancel: tooltip ? () => setTooltipVisible(false) : void 0
    }
  );
  if (!mounted || !tooltip) return rect;
  const tooltipLayer = document.getElementById(tooltipLayerId);
  return /* @__PURE__ */ jsxs20(Fragment2, { children: [
    rect,
    tooltipLayer ? createPortal(
      /* @__PURE__ */ jsx22(
        Tooltip,
        {
          id: tooltipId,
          row,
          column,
          visible: tooltipVisible,
          parentWidth,
          children: tooltip
        }
      ),
      tooltipLayer
    ) : null
  ] });
}
function Tooltip({
  id,
  row,
  column,
  children,
  visible,
  parentWidth
}) {
  const textRef = useRef2(null);
  const [rect, setRect] = useState5({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    arrowX: 0
  });
  useEffect(() => {
    if (!visible || textRef.current === null) return;
    const textRect = textRef.current.getBBox();
    const width = textRect.width + 12;
    const height = textRect.height + 12;
    const arrowX = OFFSET.x + CELL_WIDTH * column + CELL_GAP * column - width / 2;
    const y = OFFSET.y + CELL_HEIGHT * row + CELL_GAP * row - height - 2;
    let x = arrowX;
    if (x < 0) x = 20;
    if (x + width > parentWidth) x = parentWidth - width;
    x = Math.max(0, x);
    setRect({ x, y, width, height, arrowX });
  }, [children, column, parentWidth, row, visible]);
  return /* @__PURE__ */ jsxs20(
    "g",
    {
      id,
      role: "tooltip",
      className: `paper-heatmap__tooltip${visible ? "" : " hidden"}`,
      "aria-hidden": !visible,
      children: [
        /* @__PURE__ */ jsx22(
          "rect",
          {
            width: rect.width,
            height: rect.height,
            x: rect.x,
            y: rect.y,
            className: "paper-heatmap__tooltip-background"
          }
        ),
        /* @__PURE__ */ jsx22(
          "polygon",
          {
            points: pointsForRect(rect),
            className: "paper-heatmap__tooltip-background"
          }
        ),
        /* @__PURE__ */ jsx22(
          "text",
          {
            ref: textRef,
            x: rect.x + 6,
            y: rect.y + 8,
            dominantBaseline: "hanging",
            className: "paper-heatmap__tooltip-text",
            children
          }
        )
      ]
    }
  );
}
function pointsForRect({
  y,
  width,
  height,
  arrowX
}) {
  const size = 8;
  const middle = arrowX + width / 2 + CELL_WIDTH / 2;
  const left = middle - size / 2;
  const right = middle + size / 2;
  const top = y + height - 1;
  const bottom = top + 5;
  return `${left},${top} ${right},${top} ${middle},${bottom}`;
}
function getCellDepth(maxValue, value) {
  if (value === void 0) return "empty";
  if (value === 0) return "0";
  const ratio = maxValue === 0 ? 0 : value / maxValue;
  if (ratio < 0.25) return "1";
  if (ratio < 0.5) return "2";
  if (ratio < 0.75) return "3";
  return "4";
}

// src/components/navigation/Navbar.tsx
import { Menu as Menu2 } from "@base-ui/react/menu";
import {
  useId as useId9,
  useRef as useRef3,
  useState as useState6,
  useSyncExternalStore
} from "react";

// src/components/navigation/navigation.tsx
import {
  cloneElement
} from "react";
import { jsx as jsx23 } from "react/jsx-runtime";
function defaultRenderLink(props) {
  return /* @__PURE__ */ jsx23("a", { ...props });
}
function renderNavigationLink(renderLink, props, key) {
  const element = (renderLink ?? defaultRenderLink)(props);
  return key === void 0 ? element : cloneElement(element, { key });
}
function disabledLinkClick(event) {
  event.preventDefault();
}
function linkIsCurrent(item, currentPath) {
  return item.current ?? (currentPath !== void 0 && item.href === currentPath);
}

// src/components/navigation/Navbar.tsx
import { Fragment as Fragment3, jsx as jsx24, jsxs as jsxs21 } from "react/jsx-runtime";
function noSubscription() {
  return () => {
  };
}
function NavbarDropdownMenu({
  item,
  currentPath,
  renderLink
}) {
  const [portalContainer, setPortalContainer] = useState6(null);
  return /* @__PURE__ */ jsxs21(Menu2.Root, { children: [
    /* @__PURE__ */ jsxs21(
      Menu2.Trigger,
      {
        ref: (node) => {
          if (node && node.ownerDocument.body !== portalContainer) {
            setPortalContainer(node.ownerDocument.body);
          }
        },
        className: "paper-navbar__dropdown-trigger",
        children: [
          /* @__PURE__ */ jsx24("span", { children: item.label }),
          /* @__PURE__ */ jsx24(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" })
        ]
      }
    ),
    /* @__PURE__ */ jsx24(Menu2.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx24(Menu2.Positioner, { className: "paper-navbar__positioner", sideOffset: 6, align: "end", children: /* @__PURE__ */ jsx24(Menu2.Popup, { className: "paper-navbar__dropdown paper-elevation-floating", children: item.links.map((link) => {
      const current = linkIsCurrent(link, currentPath);
      if (link.disabled) {
        return /* @__PURE__ */ jsxs21(
          Menu2.Item,
          {
            className: "paper-navbar__dropdown-item",
            disabled: true,
            label: link.label,
            children: [
              link.icon ? /* @__PURE__ */ jsx24("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
              /* @__PURE__ */ jsx24("span", { children: link.label })
            ]
          },
          link.id
        );
      }
      return /* @__PURE__ */ jsx24(
        Menu2.LinkItem,
        {
          className: "paper-navbar__dropdown-item",
          label: link.label,
          closeOnClick: true,
          "aria-current": current ? "page" : void 0,
          onClick: link.onSelect,
          render: renderNavigationLink(renderLink, {
            href: link.href,
            children: /* @__PURE__ */ jsxs21(Fragment3, { children: [
              link.icon ? /* @__PURE__ */ jsx24("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
              /* @__PURE__ */ jsx24("span", { children: link.label })
            ] })
          })
        },
        link.id
      );
    }) }) }) })
  ] });
}
function Navbar({
  navigation,
  brand,
  brandHref,
  actions,
  mobileFooter,
  mobileNavigation = true,
  currentPath,
  renderLink,
  label = "Main navigation",
  menuLabel = "Menu",
  isLoading = false
}) {
  const menuId = `paper-navbar-${useId9().replace(/:/gu, "")}`;
  const [mobileOpen, setMobileOpen] = useState6(false);
  const hydrated = useSyncExternalStore(noSubscription, () => true, () => false);
  const mobileTriggerRef = useRef3(null);
  const closeMobile = () => setMobileOpen(false);
  const onMobileClick = (event) => {
    const link = event.target.closest("a[href]");
    if (link && event.currentTarget.contains(link) && link.getAttribute("aria-disabled") !== "true") closeMobile();
  };
  const onMobileKeyDown = (event) => {
    if (event.key !== "Escape") return;
    setMobileOpen(false);
    mobileTriggerRef.current?.focus();
  };
  return /* @__PURE__ */ jsxs21("nav", { className: "paper-navbar", "aria-label": label, onKeyDown: mobileNavigation && mobileOpen ? onMobileKeyDown : void 0, onClick: mobileNavigation && mobileOpen ? onMobileClick : void 0, children: [
    /* @__PURE__ */ jsxs21("div", { className: "paper-navbar__inner", children: [
      /* @__PURE__ */ jsx24("div", { className: "paper-navbar__brand", children: renderNavigationLink(renderLink, { href: brandHref, children: brand }) }),
      navigation.length > 0 ? /* @__PURE__ */ jsx24("div", { className: "paper-navbar__desktop-links", children: navigation.map((item) => {
        if (item.type === "dropdown") {
          return /* @__PURE__ */ jsx24(NavbarDropdownMenu, { item, currentPath, renderLink }, item.id);
        }
        const current = linkIsCurrent(item, currentPath);
        return renderNavigationLink(renderLink, {
          href: item.href,
          className: "paper-navbar__link",
          "aria-current": current ? "page" : void 0,
          "aria-disabled": item.disabled || void 0,
          tabIndex: item.disabled ? -1 : void 0,
          onClick: item.disabled ? disabledLinkClick : () => item.onSelect?.(),
          children: item.label
        }, item.id);
      }) }) : null,
      actions ? /* @__PURE__ */ jsx24("div", { className: "paper-navbar__actions", children: actions }) : null,
      mobileNavigation ? /* @__PURE__ */ jsx24(
        "button",
        {
          ref: mobileTriggerRef,
          type: "button",
          className: "paper-navbar__mobile-trigger",
          "aria-controls": menuId,
          "aria-expanded": mobileOpen,
          "aria-label": mobileOpen ? `Close ${menuLabel.toLocaleLowerCase()}` : `Open ${menuLabel.toLocaleLowerCase()}`,
          disabled: !hydrated,
          onClick: () => setMobileOpen((open) => !open),
          children: mobileOpen ? /* @__PURE__ */ jsx24(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" }) : /* @__PURE__ */ jsx24(Bars3Icon, { className: iconClassName("prominent"), "aria-hidden": "true" })
        }
      ) : null
    ] }),
    mobileNavigation ? /* @__PURE__ */ jsxs21(
      "div",
      {
        id: menuId,
        className: "paper-navbar__mobile-panel",
        "data-open": mobileOpen ? "true" : "false",
        hidden: !mobileOpen,
        children: [
          navigation.map(
            (item) => item.type === "link" ? /* @__PURE__ */ jsx24("div", { children: renderNavigationLink(renderLink, {
              href: item.href,
              className: "paper-navbar__mobile-link",
              "aria-current": linkIsCurrent(item, currentPath) ? "page" : void 0,
              "aria-disabled": item.disabled || void 0,
              tabIndex: item.disabled ? -1 : void 0,
              onClick: item.disabled ? disabledLinkClick : () => {
                item.onSelect?.();
                closeMobile();
              },
              children: item.label
            }) }, item.id) : /* @__PURE__ */ jsxs21("section", { className: "paper-navbar__mobile-group", "aria-labelledby": `${menuId}-${item.id}`, children: [
              /* @__PURE__ */ jsx24("h2", { id: `${menuId}-${item.id}`, children: item.label }),
              item.links.map(
                (link) => renderNavigationLink(renderLink, {
                  href: link.href,
                  className: "paper-navbar__mobile-link",
                  "aria-current": linkIsCurrent(link, currentPath) ? "page" : void 0,
                  "aria-disabled": link.disabled || void 0,
                  tabIndex: link.disabled ? -1 : void 0,
                  onClick: link.disabled ? disabledLinkClick : () => {
                    link.onSelect?.();
                    closeMobile();
                  },
                  children: /* @__PURE__ */ jsxs21(Fragment3, { children: [
                    link.icon ? /* @__PURE__ */ jsx24("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
                    /* @__PURE__ */ jsx24("span", { children: link.label })
                  ] })
                }, link.id)
              )
            ] }, item.id)
          ),
          mobileFooter?.(closeMobile)
        ]
      }
    ) : null,
    isLoading ? /* @__PURE__ */ jsx24("div", { className: "paper-navbar__loading", role: "status", children: /* @__PURE__ */ jsx24("span", { className: "paper-sr-only", children: "Loading navigation" }) }) : null
  ] });
}

// src/components/navigation/Sidebar.tsx
import { useId as useId10 } from "react";
import { Fragment as Fragment4, jsx as jsx25, jsxs as jsxs22 } from "react/jsx-runtime";
function sidebarSectionHeadingId(sidebarId, sectionId) {
  const suffix = sectionId.replace(/[^a-zA-Z0-9_-]+/gu, "-").replace(/^-+|-+$/gu, "") || "section";
  return `${sidebarId}-${suffix}`;
}
function Sidebar({
  sections,
  currentPath,
  renderLink,
  label = "Section navigation"
}) {
  const sidebarId = `paper-sidebar-${useId10().replace(/:/gu, "")}`;
  return /* @__PURE__ */ jsx25("nav", { className: "paper-sidebar", "aria-label": label, children: sections.map((section) => /* @__PURE__ */ jsxs22("section", { className: "paper-sidebar__section", "aria-labelledby": sidebarSectionHeadingId(sidebarId, section.id), children: [
    /* @__PURE__ */ jsx25("h2", { id: sidebarSectionHeadingId(sidebarId, section.id), className: "paper-sidebar__title", children: section.title }),
    /* @__PURE__ */ jsx25("ul", { className: "paper-sidebar__list", children: section.links.map((link) => {
      const current = linkIsCurrent(link, currentPath);
      return /* @__PURE__ */ jsx25("li", { children: renderNavigationLink(renderLink, {
        href: link.href,
        className: "paper-sidebar__link",
        "aria-current": current ? "page" : void 0,
        "aria-disabled": link.disabled || void 0,
        tabIndex: link.disabled ? -1 : void 0,
        onClick: link.disabled ? disabledLinkClick : () => link.onSelect?.(),
        children: /* @__PURE__ */ jsxs22(Fragment4, { children: [
          link.icon ? /* @__PURE__ */ jsx25("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
          /* @__PURE__ */ jsx25("span", { children: link.label })
        ] })
      }) }, link.id);
    }) })
  ] }, section.id)) });
}

// src/components/navigation/Breadcrumb.tsx
import { Fragment as Fragment5, jsx as jsx26, jsxs as jsxs23 } from "react/jsx-runtime";
function Breadcrumb({ items, renderLink, label = "Breadcrumb" }) {
  return /* @__PURE__ */ jsx26("nav", { className: "paper-breadcrumb", "aria-label": label, children: /* @__PURE__ */ jsx26("ol", { className: "paper-breadcrumb__list", children: items.map((item, index) => {
    const current = index === items.length - 1;
    return /* @__PURE__ */ jsxs23("li", { className: "paper-breadcrumb__item", children: [
      current || !item.href ? /* @__PURE__ */ jsxs23("span", { className: "paper-breadcrumb__current", "aria-current": current ? "page" : void 0, children: [
        item.icon ? /* @__PURE__ */ jsx26("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: item.icon }) : null,
        /* @__PURE__ */ jsx26("span", { children: item.label })
      ] }) : renderNavigationLink(renderLink, {
        href: item.href,
        className: "paper-breadcrumb__link",
        children: /* @__PURE__ */ jsxs23(Fragment5, { children: [
          item.icon ? /* @__PURE__ */ jsx26("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: item.icon }) : null,
          /* @__PURE__ */ jsx26("span", { children: item.label })
        ] })
      }),
      !current ? /* @__PURE__ */ jsx26(ChevronRightIcon, { className: "paper-breadcrumb__separator paper-icon-compact", "aria-hidden": "true" }) : null
    ] }, item.id);
  }) }) });
}

// src/components/navigation/Tabbar.tsx
import { useEffect as useEffect2, useRef as useRef4 } from "react";
import { Fragment as Fragment6, jsx as jsx27, jsxs as jsxs24 } from "react/jsx-runtime";
function TabbarList({
  links,
  currentPath,
  renderLink,
  orientation
}) {
  const listRef = useRef4(null);
  const currentId = links.find((link) => linkIsCurrent(link, currentPath))?.id;
  useEffect2(() => {
    const list = listRef.current;
    if (!list || orientation !== "horizontal" || !currentId) return;
    const revealCurrent = () => {
      const current = list.querySelector('[aria-current="page"]');
      if (!current) return;
      const viewport = list.getBoundingClientRect();
      const destination = current.getBoundingClientRect();
      if (destination.left < viewport.left) list.scrollLeft += destination.left - viewport.left;
      else if (destination.right > viewport.right) list.scrollLeft += destination.right - viewport.right;
    };
    revealCurrent();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(revealCurrent);
    observer.observe(list);
    return () => observer.disconnect();
  }, [currentId, orientation]);
  return /* @__PURE__ */ jsx27("ul", { ref: listRef, className: `paper-tabbar__list paper-tabbar__list--${orientation}`, children: links.map((link) => {
    const current = linkIsCurrent(link, currentPath);
    return /* @__PURE__ */ jsx27("li", { children: renderNavigationLink(renderLink, {
      href: link.href,
      className: "paper-tabbar__link",
      "aria-current": current ? "page" : void 0,
      "aria-disabled": link.disabled || void 0,
      tabIndex: link.disabled ? -1 : void 0,
      onClick: link.disabled ? disabledLinkClick : () => link.onSelect?.(),
      children: /* @__PURE__ */ jsxs24(Fragment6, { children: [
        link.icon ? /* @__PURE__ */ jsx27("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
        /* @__PURE__ */ jsx27("span", { children: link.label })
      ] })
    }) }, link.id);
  }) });
}
function Tabbar({ label = "Page sections", ...props }) {
  return /* @__PURE__ */ jsx27("nav", { className: "paper-tabbar", "aria-label": label, children: /* @__PURE__ */ jsx27(TabbarList, { ...props, label, orientation: "horizontal" }) });
}
function VerticalTabbar({ label = "Page sections", ...props }) {
  return /* @__PURE__ */ jsx27("nav", { className: "paper-tabbar paper-tabbar--vertical", "aria-label": label, children: /* @__PURE__ */ jsx27(TabbarList, { ...props, label, orientation: "vertical" }) });
}

// src/components/navigation/Pagination.tsx
import { Fragment as Fragment7, jsx as jsx28, jsxs as jsxs25 } from "react/jsx-runtime";
function paginationTokens(totalPages, currentPage, siblingCount) {
  const visible = /* @__PURE__ */ new Set([1, totalPages]);
  for (let page = currentPage - siblingCount; page <= currentPage + siblingCount; page += 1) {
    if (page >= 1 && page <= totalPages) visible.add(page);
  }
  const pages = [...visible].sort((left, right) => left - right);
  const tokens = [];
  pages.forEach((page, index) => {
    const previous = pages[index - 1];
    if (previous !== void 0 && page - previous > 1) {
      tokens.push(previous === 1 ? "start-gap" : "end-gap");
    }
    tokens.push(page);
  });
  return tokens;
}
function Pagination({
  totalPages,
  currentPage,
  getHref,
  onPageChange,
  siblingCount = 2,
  label = "Pagination",
  renderLink
}) {
  if (!Number.isInteger(totalPages) || totalPages < 1) {
    throw new RangeError("Pagination totalPages must be a positive integer.");
  }
  if (!Number.isInteger(currentPage) || currentPage < 1 || currentPage > totalPages) {
    throw new RangeError("Pagination currentPage must be within totalPages.");
  }
  if (!Number.isInteger(siblingCount) || siblingCount < 0) {
    throw new RangeError("Pagination siblingCount must be a non-negative integer.");
  }
  if (!getHref && !onPageChange) {
    throw new Error("Pagination requires getHref or onPageChange.");
  }
  const activate = (page) => (event) => {
    if (!onPageChange || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onPageChange(page);
  };
  const tokens = paginationTokens(totalPages, currentPage, siblingCount);
  const control = (page, content, controlLabel, disabled) => {
    if (disabled) {
      return /* @__PURE__ */ jsx28("span", { className: "paper-pagination__control", "aria-disabled": "true", children: content });
    }
    if (getHref) {
      return renderNavigationLink(renderLink, {
        href: getHref(page),
        className: "paper-pagination__control",
        "aria-label": controlLabel,
        onClick: activate(page),
        children: content
      });
    }
    return /* @__PURE__ */ jsx28("button", { type: "button", className: "paper-pagination__control", "aria-label": controlLabel, onClick: () => onPageChange?.(page), children: content });
  };
  return /* @__PURE__ */ jsx28("nav", { className: "paper-pagination", "aria-label": label, children: /* @__PURE__ */ jsxs25("ul", { className: "paper-pagination__list", children: [
    /* @__PURE__ */ jsx28("li", { children: control(currentPage - 1, /* @__PURE__ */ jsxs25(Fragment7, { children: [
      /* @__PURE__ */ jsx28(ChevronLeftIcon, { className: iconClassName("compact"), "aria-hidden": "true" }),
      /* @__PURE__ */ jsx28("span", { children: "Previous" })
    ] }), "Previous page", currentPage === 1) }),
    tokens.map(
      (token) => typeof token === "string" ? /* @__PURE__ */ jsx28("li", { className: "paper-pagination__gap", "aria-hidden": "true", children: /* @__PURE__ */ jsx28(EllipsisHorizontalIcon, { className: iconClassName("default") }) }, token) : /* @__PURE__ */ jsx28("li", { children: token === currentPage ? /* @__PURE__ */ jsx28("span", { className: "paper-pagination__page", "aria-current": "page", "aria-label": `Page ${token}`, children: token }) : getHref ? renderNavigationLink(renderLink, {
        href: getHref(token),
        className: "paper-pagination__page",
        "aria-label": `Page ${token}`,
        onClick: activate(token),
        children: token
      }) : /* @__PURE__ */ jsx28("button", { type: "button", className: "paper-pagination__page", "aria-label": `Page ${token}`, onClick: () => onPageChange?.(token), children: token }) }, token)
    ),
    /* @__PURE__ */ jsx28("li", { children: control(currentPage + 1, /* @__PURE__ */ jsxs25(Fragment7, { children: [
      /* @__PURE__ */ jsx28("span", { children: "Next" }),
      /* @__PURE__ */ jsx28(ChevronRightIcon, { className: iconClassName("compact"), "aria-hidden": "true" })
    ] }), "Next page", currentPage === totalPages) })
  ] }) });
}

// src/components/navigation/Tabs/Tabs.tsx
import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import {
  forwardRef as forwardRef3
} from "react";
import { jsx as jsx29 } from "react/jsx-runtime";
function classes(base, className) {
  return [base, className].filter(Boolean).join(" ");
}
function moveTabFocus(event, loopFocus) {
  const tabs = Array.from(
    event.currentTarget.querySelectorAll(
      '[role="tab"]:not([aria-disabled="true"])'
    )
  );
  const currentIndex = tabs.indexOf(event.target);
  if (currentIndex < 0 || tabs.length === 0) return;
  let nextIndex;
  const vertical = event.currentTarget.dataset.orientation === "vertical";
  if (event.key === "Home") nextIndex = 0;
  if (event.key === "End") nextIndex = tabs.length - 1;
  if (event.key === (vertical ? "ArrowDown" : "ArrowRight")) {
    nextIndex = currentIndex + 1;
  }
  if (event.key === (vertical ? "ArrowUp" : "ArrowLeft")) {
    nextIndex = currentIndex - 1;
  }
  if (nextIndex === void 0) return;
  if (loopFocus) nextIndex = (nextIndex + tabs.length) % tabs.length;
  else nextIndex = Math.max(0, Math.min(nextIndex, tabs.length - 1));
  event.preventDefault();
  event.stopPropagation();
  tabs[nextIndex]?.focus();
}
var TabsRoot = forwardRef3(function TabsRoot2({ className, onValueChange, ...props }, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.Root,
    {
      ref,
      className: classes("paper-tabs", className),
      onValueChange: (nextValue) => {
        if (typeof nextValue === "string") onValueChange?.(nextValue);
      },
      ...props
    }
  );
});
var TabsList = forwardRef3(function TabsList2({
  activateOnFocus = true,
  className,
  loopFocus = true,
  onKeyDownCapture,
  ...props
}, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.List,
    {
      ref,
      activateOnFocus,
      loopFocus,
      className: classes("paper-tabs__list", className),
      onKeyDownCapture: (event) => {
        onKeyDownCapture?.(event);
        if (!event.defaultPrevented) moveTabFocus(event, loopFocus);
      },
      ...props
    }
  );
});
var TabsTab = forwardRef3(function TabsTab2({ className, type = "button", ...props }, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.Tab,
    {
      ref,
      type,
      className: classes("paper-tabs__tab", className),
      ...props
    }
  );
});
var TabsPanel = forwardRef3(function TabsPanel2({ className, keepMounted = true, value, ...props }, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.Panel,
    {
      ref,
      className: classes("paper-tabs__panel", className),
      "data-value": String(value),
      keepMounted,
      value,
      ...props
    }
  );
});
var Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel
};
export {
  ActionMenu,
  AmountWithUnit,
  AppliedFilters,
  AutocompleteInput,
  AutocompleteMultiInput,
  BUTTON_VARIANTS,
  Breadcrumb,
  Button,
  ButtonGroup,
  Checkbox,
  DRAWER_PLACEMENTS,
  DescriptionList,
  Drawer,
  EmptyState,
  FLASH_VARIANTS,
  Flash,
  HeatmapChart,
  Input,
  Loading,
  Modal,
  Navbar,
  PageHeader,
  Pagination,
  RADIO_SELECT_VARIANTS,
  RadioGroup,
  RadioSelect,
  SectionHeading,
  Select,
  Sidebar,
  StatGrid,
  StatusTag,
  Surface,
  Tabbar,
  Table,
  Tabs,
  TabsList,
  TabsPanel,
  TabsRoot,
  TabsTab,
  TagsInput,
  TextArea,
  ToastContainer,
  ToastProvider,
  ToggleSelect,
  VerticalTabbar,
  buttonClassName,
  chartCategoryStyles,
  chartPalette,
  chartSeries,
  surfaceClassName,
  usePaperForm,
  useToast,
  withPaperForm
};
//# sourceMappingURL=index.js.map